import React, { useState, useEffect } from 'react';
import { Typography } from 'antd';
import * as changeCase from 'change-case';
import useJobsStore from 'Store/useJobsStore';
import useAuthStore from 'Store/useAuthStore';
import useEntitlementsStore from 'Store/useEntitlementsStore';
import {
  getVersions,
  getSBJobDescription,
  getRnCVersions,
  getCompareData,
  compareMarkdowns,
} from 'Api/apiService';
import { userHasEntitlement } from 'Utils/parseEntitlements';
import { COMPARE_VIEW, COMPARE_VIEW_LABELS, COMPARE_LOADING, COMPARE_ENTRY, JD_STATUSES } from 'constants/jobs.constants';
import { getOrderedComparisonKeys } from 'Pages/SbJobCompare/summary.uils';
import analytics, { ANALYTICS_EVENTS } from 'Utils/analytics';
import SmartDiffViewerV3 from 'Components/SbDiffCheckerV3/SmartDiffViewerV3';
import { SbSelect, SbSwitch, SbSpin, SbButton, SbEmpty, SbTable, SbRow, SbCol, SbTooltip, SbNotification } from 'uiCore';
import {
  ToolbarRowStyled,
  ViewToggleStyled,
  ViewLabelStyled,
  BodyStyled,
  SummaryWrapperStyled,
  CenterStyled,
  CompareBtnWrapperStyled,
  SpinCenterStyled,
  SummaryListStyled,
} from './DiffCheckerV2.styles';

const TOOLTIP_DIFF_VIEW = "Show a detailed block-by-block view of exactly what changed between the two selected JDs.";
const TOOLTIP_SUMMARY_VIEW = "Show a high-level written summary of the key differences between the two selected JDs.";

const { Text } = Typography;

const toVersionOptions = (data) => data.map(v => ({ value: v.sb_unique_id, label: `V${v.version}`, versionData: v }));

const resolveLatestPublishedVersion = (versions) => {
  const published = versions.filter(v => v.jd_status === JD_STATUSES.PUBLISHED);
  if (!published.length) return null;
  return published.reduce((latest, v) => (v.version > latest.version ? v : latest), published[0]);
};

const DiffCheckerV2 = ({
  viewMode,
  setViewMode,
  leftState,
  setLeftState,
  rightState,
  setRightState,
  entryFrom,
}) => {
  const email = useAuthStore((s) => s.email);
  const entitlementsData = useEntitlementsStore((s) => s.entitlementsData);
  const profileRole = useEntitlementsStore((s) => s.profileRole);
  const compareViewSelection = useJobsStore((s) => s.compareViewSelection);
  const jobData = useJobsStore((s) => s.jobData);
  const selectedJob = useJobsStore((s) => s.selectedJob);

  const { selectedJob: leftSelectedJob, versionData: leftVersionData, selectedVersion: leftSelectedVersion } = leftState;
  const { selectedJob: rightSelectedJob, versionData: rightVersionData, selectedVersion: rightSelectedVersion } = rightState;

  const [jdPair, setJdPair] = useState({ left: '', right: '' });
  const [compareResult, setCompareResult] = useState({ diff: null, summary: {} });
  const [compareLoading, setCompareLoading] = useState(null);
  const [isLoadingLeftVersions, setIsLoadingLeftVersions] = useState(false);
  const [isLoadingRightVersions, setIsLoadingRightVersions] = useState(false);
  const [loadingJDCount, setLoadingJDCount] = useState(0);

  // Clear any previous diff when the mode or either selection changes. The diff
  // itself is run by the Compare button (fetchDiffData), the same way the
  // summary is — see handleCompare.
  useEffect(() => {
    setCompareResult(r => ({ ...r, diff: null }));
  }, [jdPair, viewMode]);

  useEffect(() => {
    if (isLibraryEntry) return;
    if (selectedJob && !leftSelectedJob) handleJobSelect('left', selectedJob.job_id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewMode === COMPARE_VIEW.SUMMARY) {
      if (leftSelectedJob?.data?.job_id && !leftVersionData.length) fetchVersions(leftSelectedJob.data.job_id, 'left');
      if (rightSelectedJob?.data?.job_id && !rightVersionData.length) fetchVersions(rightSelectedJob.data.job_id, 'right');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const isRnC = () => userHasEntitlement(entitlementsData, profileRole, 'jobs_library-version', 'audit');
  const isLibraryEntry = entryFrom === COMPARE_ENTRY.JOBS_LIBRARY;
  const isJobDetailsEntry = entryFrom === COMPARE_ENTRY.JOB_DETAILS;
  // Both in-app entry points show the simplified view: no version dropdowns, JD
  // options limited to published, and versions auto-resolved to the latest
  // published one. The standalone compare route keeps manual version picking.
  const isSimplified = isLibraryEntry || isJobDetailsEntry;

  const sides = {
    left: {
      selectedJob: leftSelectedJob,
      selectedVersion: leftSelectedVersion,
      versionOptions: toVersionOptions(leftVersionData),
      isLoadingVersions: isLoadingLeftVersions,
      setIsLoadingVersions: setIsLoadingLeftVersions,
      setState: setLeftState,
    },
    right: {
      selectedJob: rightSelectedJob,
      selectedVersion: rightSelectedVersion,
      versionOptions: toVersionOptions(rightVersionData),
      isLoadingVersions: isLoadingRightVersions,
      setIsLoadingVersions: setIsLoadingRightVersions,
      setState: setRightState,
    },
  };

  const resetDiff = () => setCompareResult({ diff: null, summary: {} });

  const fetchVersions = async (jobId, side) => {
    const s = sides[side];
    s.setIsLoadingVersions(true);
    try {
      if (isSimplified) {
        const res = isRnC() ? await getRnCVersions(email, jobId) : await getVersions(email, jobId);
        const latestPublished = resolveLatestPublishedVersion(res || []);
        s.setState(prev => ({
          ...prev,
          versionData: latestPublished ? [latestPublished] : [],
          selectedVersion: latestPublished
            ? { value: latestPublished.sb_unique_id, label: `V${latestPublished.version}`, data: latestPublished }
            : null,
        }));
        if (latestPublished) {
          // Awaited so the dropdown's loading state (below) spans version resolution AND
          // content fetch — it only clears once the JD text has loaded or an error fires.
          await fetchJD(latestPublished.sb_unique_id, side);
        } else {
          setJdPair(p => ({ ...p, [side]: '' }));
          const roleName = jobOptions.find(o => o.value === jobId)?.jobData?.job_title;
          SbNotification.warning(
            'Not published yet',
            roleName
              ? `"${roleName}" has no published version, so it can’t be compared.`
              : 'This job description has no published version, so it can’t be compared.'
          );
        }
        return;
      }

      const res = isRnC() ? await getRnCVersions(email, jobId) : await getVersions(email, jobId);
      if (res) {
        const isPreselect = side === 'left' && compareViewSelection !== '' && selectedJob?.job_id === jobId;
        s.setState(prev => ({
          ...prev,
          versionData: res,
          ...(isPreselect && {
            selectedVersion: { value: selectedJob.sb_unique_id, label: `V${selectedJob.version}`, data: selectedJob },
          }),
        }));
        if (isPreselect) fetchJD(selectedJob.sb_unique_id, 'left');
      }
    } catch {
      s.setState(prev => ({ ...prev, versionData: [] }));
    } finally {
      s.setIsLoadingVersions(false);
    }
  };

  const fetchJD = async (sbUniqueId, side) => {
    setLoadingJDCount(c => c + 1);
    try {
      const res = await getSBJobDescription(sbUniqueId, email);
      setJdPair(p => ({ ...p, [side]: res?.job_description || '' }));
    } catch {
      setJdPair(p => ({ ...p, [side]: '' }));
    } finally {
      setLoadingJDCount(c => c - 1);
    }
  };

  const fetchDiffData = async () => {
    setCompareLoading(COMPARE_LOADING.AI);
    analytics.trackCompareMarkdown({
      from: 'Compare Job Descriptions',
      email,
      description: ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.COMPARE_MARKDOWN,
    });
    try {
      const diff = await compareMarkdowns(jdPair.left, jdPair.right);
      setCompareResult(r => ({ ...r, diff }));
    } catch (err) {
      console.error('AI Comparison Failed', err);
    } finally {
      setCompareLoading(null);
    }
  };

  const fetchCompareData = async () => {
    setCompareLoading(COMPARE_LOADING.SUMMARY);
    try {
      const res = await getCompareData(
        leftSelectedVersion.data.sb_unique_id,
        rightSelectedVersion.data.sb_unique_id,
        email
      );
      if (res) setCompareResult(r => ({ ...r, summary: res.comparison_result.difference_table.jobDescriptionComparison }));
    } catch (err) {
      console.error('Compare data failed', err);
    } finally {
      setCompareLoading(null);
    }
  };

  // Only the requisition shape carries jd_status. Job-code view supplies job-code
  // groups (no jd_status), so that shape is left untouched and behaves as before.
  // Filtering is limited to the Jobs Library entry path, the only one that
  // auto-resolves a published version; the standalone compare screen still lets
  // users pick any version by hand.
  const jobOptions = jobData
    .filter(job => !(isSimplified && job.jd_status) || job.jd_status === JD_STATUSES.PUBLISHED)
    .map(job => ({
      value: job.job_id,
      label: `${job.sb_requisition_number || job.job_code} - ${job.job_title}`,
      jobData: job,
    }));

  const handleJobSelect = (side, value) => {
    const s = sides[side];
    const opt = jobOptions.find(o => o.value === value);
    s.setState({
      selectedJob: opt ? { value, label: opt.label, data: opt.jobData } : null,
      versionData: [],
      selectedVersion: null,
    });
    setJdPair(p => ({ ...p, [side]: '' }));
    resetDiff();
    if (opt?.jobData) fetchVersions(opt.jobData.job_id, side);
  };

  const handleVersionSelect = (side, value) => {
    const s = sides[side];
    const opt = s.versionOptions.find(o => o.value === value);
    s.setState(prev => ({ ...prev, selectedVersion: opt ? { value, label: opt.label, data: opt.versionData } : null }));
    setJdPair(p => ({ ...p, [side]: '' }));
    resetDiff();
    if (opt?.versionData) fetchJD(opt.versionData.sb_unique_id, side);
  };

  const handleViewToggle = (checked) => {
    const next = checked ? COMPARE_VIEW.SUMMARY : COMPARE_VIEW.DIFFERENCE;
    setViewMode(next);
    resetDiff();
    if (next === COMPARE_VIEW.SUMMARY) {
      analytics.trackComparJobsChecker({
        from: 'Compare Job Descriptions',
        email,
        description: ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.COMPARE_JOBS_DIFFERENCE_CHECKER,
      });
    }
  };

  const handleCompare = () => {
    if (!isCompareReady) return;
    analytics.trackComparJobs({
      from: viewMode,
      email,
      description: ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.COMPARE_JOBS,
    });
    if (isSummaryMode) fetchCompareData();
    else fetchDiffData();
  };

  const isCompareReady = Boolean(
    leftSelectedJob && leftSelectedVersion && rightSelectedJob && rightSelectedVersion
    && jdPair.left && jdPair.right
  );
  const isFetchingJDContent = loadingJDCount > 0;
  const compareDisabledReason = isCompareReady || compareLoading === COMPARE_LOADING.SUMMARY
    ? ''
    : (!leftSelectedJob || !rightSelectedJob)
      ? 'Select job descriptions on both sides to compare'
      : 'Waiting for both job descriptions to finish loading';
  const isSummaryMode = viewMode === COMPARE_VIEW.SUMMARY;
  // Constant across modes: the Compare button now renders in both, so both
  // rows have the same 2 spans to give it.
  const versionSpan = 3;

  const hasUnpublishedSelection = (side) => {
    const s = sides[side];
    return isSimplified && s.selectedJob && !s.isLoadingVersions && !s.selectedVersion;
  };

  const emptyMessage = (() => {
    if (!leftSelectedJob || !rightSelectedJob) return 'Select job descriptions on both sides to compare';
    if (hasUnpublishedSelection('left') || hasUnpublishedSelection('right')) {
      return 'Selected job description has no published version to compare';
    }
    if (!leftSelectedVersion || !rightSelectedVersion) return 'Select versions on both sides to compare';
    if (isSummaryMode && !Object.keys(compareResult.summary).length) return "Click 'Compare' to view the summary";
    if (!isSummaryMode && !compareResult.diff) return "Click 'Compare' to view the differences";
    return '';
  })();

  const orderedKeys = isCompareReady && Object.keys(compareResult.summary).length > 0
    ? getOrderedComparisonKeys({ comparisonSummary: compareResult.summary, leftSelectedJob, rightSelectedJob, leftSelectedVersion, rightSelectedVersion })
    : [];

  const cellProps = () => ({ style: { padding: '28px 31px', verticalAlign: 'baseline' } });
  const headerCellProps = () => ({ style: { padding: '14px 31px' } });

  const summaryColumns = orderedKeys.length > 0 ? [
    {
      title: <Text strong>Feature</Text>,
      dataIndex: 'feature',
      key: 'feature',
      width: 200,
      align: 'left',
      onHeaderCell: headerCellProps,
      onCell: cellProps,
      render: (val) => <Text strong>{val}</Text>,
    },
    ...orderedKeys.map(key => ({
      title: <Text strong>{key}</Text>,
      dataIndex: key,
      key,
      align: 'left',
      onHeaderCell: headerCellProps,
      onCell: cellProps,
      render: (val) => Array.isArray(val)
        ? (
          <SummaryListStyled>
            {val.map((item, i) => (
              <li key={i}>
                <Text>{changeCase.sentenceCase(String(item))}</Text>
              </li>
            ))}
          </SummaryListStyled>
        )
        : <Text>{changeCase.sentenceCase(String(val || ''))}</Text>,
    })),
  ] : [];

  const summaryDataSource = orderedKeys.length > 0
    ? Object.keys(compareResult.summary[Object.keys(compareResult.summary)[0]] || {}).map(field => {
        const row = { key: field, feature: changeCase.sentenceCase(field) };
        orderedKeys.forEach(vKey => { row[vKey] = compareResult.summary[vKey][field]; });
        return row;
      })
    : [];

  const isLoading = loadingJDCount > 0 || compareLoading !== null;

  const emptyState = <SbEmpty description={<Text type="secondary">{emptyMessage}</Text>} />;

  const jdSpan = isSimplified ? 4 + versionSpan : 4;

  const renderSideSelects = (side) => {
    const s = sides[side];
    const tooltipContent = side === 'left'
      ? 'Select the first job description to compare. Choose by job title, code, or version.'
      : 'Select the second job description to compare. Choose by job title, code, or version.';
    return (
      <>
        <SbCol xs={24} md={isSimplified ? 24 : 12} xl={jdSpan}>
          <SbTooltip content={tooltipContent}>
            <div>
              <SbSelect
                options={jobOptions}
                value={s.selectedJob?.value}
                onChange={(v) => handleJobSelect(side, v)}
                placeholder="Select Job Description..."
                optionFilterProp="label"
                loading={isSimplified && s.isLoadingVersions}
                allowClear
                showSearch
              />
            </div>
          </SbTooltip>
        </SbCol>
        {!isSimplified && (
          <SbCol xs={24} md={12} xl={versionSpan}>
            <SbTooltip content={tooltipContent}>
              <div>
                <SbSelect
                  options={s.versionOptions}
                  value={s.selectedVersion?.value}
                  onChange={(v) => handleVersionSelect(side, v)}
                  placeholder="Select Version..."
                  disabled={s.isLoadingVersions || !s.selectedJob}
                  loading={s.isLoadingVersions}
                  allowClear
                />
              </div>
            </SbTooltip>
          </SbCol>
        )}
      </>
    );
  };

  return (
    <>
      <ToolbarRowStyled>
        <SbRow gutter={[12, 8]} align="middle">
          {renderSideSelects('left')}
          <SbCol xs={24} xl={8}>
            <CenterStyled>
              <ViewToggleStyled>
                <SbTooltip content={TOOLTIP_DIFF_VIEW}>
                  <ViewLabelStyled $active={!isSummaryMode}>{COMPARE_VIEW_LABELS[COMPARE_VIEW.DIFFERENCE]}</ViewLabelStyled>
                </SbTooltip>
                <SbSwitch size="small" checked={isSummaryMode} onChange={handleViewToggle} />
                <SbTooltip content={TOOLTIP_SUMMARY_VIEW}>
                  <ViewLabelStyled $active={isSummaryMode}>{COMPARE_VIEW_LABELS[COMPARE_VIEW.SUMMARY]}</ViewLabelStyled>
                </SbTooltip>
              </ViewToggleStyled>
            </CenterStyled>
          </SbCol>
          {renderSideSelects('right')}
          <SbCol xs={24} md={12} xl={2}>
            <CompareBtnWrapperStyled>
              <SbTooltip content={compareDisabledReason}>
                <div>
                  <SbButton
                    buttonType="primary"
                    onClick={handleCompare}
                    disabled={!isCompareReady || Boolean(compareLoading)}
                    loading={Boolean(compareLoading) || (isFetchingJDContent && Boolean(leftSelectedJob) && Boolean(rightSelectedJob))}
                  >
                    Compare
                  </SbButton>
                </div>
              </SbTooltip>
            </CompareBtnWrapperStyled>
          </SbCol>
        </SbRow>
      </ToolbarRowStyled>

      <BodyStyled>
        {isLoading ? (
          <SpinCenterStyled>
            <SbSpin
              size="large"
              tip={compareLoading === COMPARE_LOADING.AI ? 'Generating AI Comparison...' : 'Loading...'}
            />
          </SpinCenterStyled>
        ) : !isSummaryMode ? (
          compareResult.diff?.diff ? (
            <SmartDiffViewerV3
              diffData={{ ...compareResult.diff, left_pane: compareResult.diff.diff.left_pane, right_pane: compareResult.diff.diff.right_pane }}
              viewMode={viewMode}
              readOnly
            />
          ) : emptyState
        ) : summaryDataSource.length > 0 ? (
          <SummaryWrapperStyled>
            <SbTable
              columns={summaryColumns}
              dataSource={summaryDataSource}
              autoFilters={false}
              pagination={false}
              rowKey="key"
              size="middle"
              sticky
            />
          </SummaryWrapperStyled>
        ) : emptyState
        }
      </BodyStyled>
    </>
  );
};

export default DiffCheckerV2;
